import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { KnowledgeBaseArticlePage } from "design-system/sites/baker/knowledge-base-article-page/knowledge-base-article-page";
import { createSiteHeaderProps } from "../universal/site-header.props";
import { createSiteFooterProps } from "../universal/site-footer.props";
import { KnowledgeBaseArticlePageEntry } from "frontend/contentful/schema/pages";
import { ContentfulHead } from "../primitives/head.contentful";
import { ContentfulPageSections } from "./page-sections.contentful";
import { usePageNavigation } from "frontend/hooks/use-page-navigation";
import { Footnotes } from "design-system/components/primitives/footnote/footnotes";
import { usePageFootnotes } from "frontend/hooks/use-page-footnotes";
import { useContentful } from "frontend/hooks/use-contentful";
import { createComponentHeaderProps } from "frontend/contentful/components/primitives/component-header.props";
import { createCtaProps } from "../blocks/cta/cta.props";
import { createCtaLinkProps } from "../primitives/cta-link.props";
import { RenderContentfulRichText } from "../utils/render-contentful-rich-text";
import { ComponentHeaderProps } from "design-system/components/primitives/component-header/component-header";
import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import { usePageProps } from "frontend/hooks/use-page-props";

export const ContentfulKnowledgeBaseArticlePage =
  createContentfulComponent<KnowledgeBaseArticlePageEntry>(
    ({ entry, createEditAttributes }) => {
      const { siteKbArticlePageSettings } = usePageProps();
      const { site, getEntries, getEntryUrl, getEntry } = useContentful();
      const { breadcrumbs } = usePageNavigation();
      const footnotes = usePageFootnotes(entry.fields.body);
      const settings = getEntry(siteKbArticlePageSettings);

      const overlineSetting = settings?.fields.siteOverlineSettings;

      const overline = entry?.fields.overline
        ? getEntry(entry?.fields.overline)
        : undefined;

      let overlineDefaultText: string | undefined = undefined;
      if (!overline && overlineSetting === "topic") {
        overlineDefaultText = entry.fields.topics?.length
          ? getEntry(entry.fields.topics[0])?.fields.title
          : undefined;
      } else if (!overline && overlineSetting === "category") {
        overlineDefaultText = entry.fields.category?.length
          ? getEntry(entry.fields.category[0])?.fields.title
          : undefined;
      }

      const teases: Array<ArticleTeaseProps> = [];

      if (entry.fields.relatedContent) {
        {
          getEntries(entry.fields.relatedContent).map((entry) =>
            teases.push({
              type: "Article",
              title: entry.fields.title
                ? entry.fields.title
                : entry.fields.text,
              link: getEntryUrl(entry),
            }),
          );
        }
      }

      const filterType = entry?.fields.category ? "category" : "topic";
      const categoryArray = getEntries(
        filterType === "category"
          ? entry.fields.category
          : entry?.fields.topics,
      );
      const categoryTitleArray = categoryArray.map(
        (category) => category.fields.title,
      );
      const category = categoryTitleArray.join(`&${filterType}=`);

      const relatedContentHeader: ComponentHeaderProps | undefined = entry
        .fields.relatedContentHeader
        ? createComponentHeaderProps(entry.fields.relatedContentHeader)
        : {
            title: "Related Content",
            linkText: "View All",
            link: undefined,
            borderPosition: "hidden",
            isSmall: false,
          };

      if (relatedContentHeader?.link) {
        relatedContentHeader.link =
          relatedContentHeader.link +
          (category ? `?${filterType}=${category}` : "");
      }
      return (
        <>
          <ContentfulHead entry={entry.fields.seo} />

          <KnowledgeBaseArticlePage
            breadcrumbs={breadcrumbs}
            topper={{
              title: entry.fields.title,
              subtitle: (
                <RenderContentfulRichText document={entry.fields.subtitle} />
              ),
              overline: {
                text: overline?.fields.text || overlineDefaultText,
                link: getEntryUrl(overline),
                editAttributes: {
                  text: createEditAttributes({
                    entry: overline,
                    fieldId: "text",
                  }),
                },
              },
              theme: entry.fields.topperTheme,
              cta: createCtaLinkProps(entry.fields.topperCta),
              editAttributes: {
                title: createEditAttributes({ entry: entry, fieldId: "title" }),
                subtitle: createEditAttributes({
                  entry: entry,
                  fieldId: "subtitle",
                }),
              },
            }}
            header={{ name: "SiteHeader", props: createSiteHeaderProps(site) }}
            footer={createSiteFooterProps(site?.fields.footer)}
            teasesHeader={relatedContentHeader}
            teases={teases}
            cta={createCtaProps(entry.fields.cta)}
          >
            <ContentfulPageSections entry={entry} />
            <Footnotes footnotes={footnotes} />
          </KnowledgeBaseArticlePage>
        </>
      );
    },
  );
